'use client';
import clsx from 'clsx';
import React from 'react';
import { withStyles } from 'tss-react/mui';

import { black } from '../../colors';
import idsTokensDefault from '../../utils/tokens/idsTokens';
import Box from '../Box';
import Divider from '../Divider';
import Icon from '../Icon';
import ListSubheader from '../ListSubheader';
import MenuItem from '../MenuItem';
import PopperMenu from '../PopperMenu';
import Typography from '../Typography';
import NavigationBarButton from './NavigationBarButton';

export const styles = ({ idsTokens = idsTokensDefault }) => ({
  NavBarMenuItem: {
    '&:not(:hover)': {
      backgroundColor: 'transparent',
    },
    /**
     * @TODO this should become unnecessary when the List component is updated
     * to not have a narrower borderBottom when pressed
     */
    borderBottomWidth: '2px !important',
  },
  link: {
    textDecoration: 'none',
    color: 'inherit',
    fontWeight: idsTokens.typography.font.weight.regular,
  },
  paper: {
    minWidth: 200,
  },
  selected: {
    fontWeight: idsTokens.typography.font.weight.medium,
    color: black,
  },
  divider: {
    margin: '8px 0px',
  },
  isTitle: {
    border: '2px solid rgba(0,0,0,0)',
    paddingTop: idsTokens.listItem.default.spacing.standard.padding.top,
    paddingRight: idsTokens.listItem.default.spacing.standard.padding.right,
    paddingBottom: idsTokens.listItem.default.spacing.standard.padding.bottom,
    paddingLeft: idsTokens.listItem.default.spacing.standard.padding.left,
  },
  menuItemDropdown: {
    margin: 0,
    padding: 16,
    borderRadius: 0,
    height: 40,
    minHeight: 'none',
    boxSizing: 'border-box',
    fontSize: idsTokens.typography.font.size.base,
    color: idsTokens.listItem.default.color.text.label.enabled,
    fontWeight: idsTokens.typography.font.weight.regular,
    lineHeight: idsTokens.typography.lineHeight.base,
    letterSpacing: idsTokens.typography.spacing.letter.lg,
    width: '100%',
    '&:hover, &.Mui-selected:hover, &:focus:not([data-value=""])': {
      backgroundColor: idsTokens.listItem.default.color.background.hover,
      color: idsTokens.listItem.default.color.text.label.focus,
    },
    '&:focus, &:focus-visible': {
      backgroundColor: idsTokens.listItem.default.color.background.focus,
      outline: `${idsTokens.functional.borderWidth.focus.default} solid ${idsTokens.functional.color.border.focus}`,
    },
    '& > .MuiSvgIcon-root': {
      transform: 'rotate(-90deg)',
    },
  },
});

export class NavigationMenuItem extends React.Component {
  state = {
    open: false,
    anchorEl: null,
    openSubMenu: false,
    subMenuAnchorEl: null,
  };

  handleClick = (e = {}) => {
    this.setState({ open: true, anchorEl: e.currentTarget });
  };

  handleRequestClose = () => {
    this.setState({ open: false, openSubMenu: false, selectedSubMenu: null });
  };

  handleSubMenuClick = (e = {}, newSelectedSubMenu) => {
    const { selectedSubMenu } = this.state;

    if (newSelectedSubMenu !== selectedSubMenu) {
      this.setState({
        open: true,
        openSubMenu: true,
        subMenuAnchorEl: e.currentTarget,
        selectedSubMenu: newSelectedSubMenu,
      });
    }
  };

  handleSubMenuRequestClose = () => {
    this.setState({ open: false, openSubMenu: false });
  };

  render() {
    const {
      classes,
      menuItems,
      onClick,
      checkIsActive,
      variant,
      slots,
      slotProps,
      ...rest
    } = this.props;
    const { open, anchorEl, openSubMenu, subMenuAnchorEl, selectedSubMenu } = this.state;
    const hasMenuItems = !!menuItems;

    return (
      <>
        <NavigationBarButton
          open={open}
          hasMenuItems={hasMenuItems}
          onClick={hasMenuItems ? undefined : onClick}
          {...rest}
          handleClick={hasMenuItems && this.handleClick}
          variant={variant}
          slots={slots}
          slotProps={slotProps}
        />
        {menuItems && (
          <PopperMenu
            open={open}
            onClose={this.handleRequestClose}
            anchorEl={anchorEl}
            classes={{
              paper: classes.paper,
            }}
            MenuListProps={{
              sx: {
                maxHeight: 400,
                overflowY: 'auto',
              },
            }}
            popperOptions={{ strategy: 'fixed' }}
            modifiers={[
              {
                name: 'offset',
                options: { offset: [0, 8] },
              },
            ]}
          >
            {menuItems.map((menuItem, i) =>
              Object.keys(menuItem).length === 0 ? (
                <Divider className={classes.divider} key={i} />
              ) : menuItem.isTitle ? (
                <ListSubheader className={classes.isTitle} key={i}>
                  <Typography variant="bodyDefault" emphasis="higher">
                    {menuItem.text}
                  </Typography>
                </ListSubheader>
              ) : menuItem.menuItems ? (
                [
                  <NavigationBarButton
                    key={i}
                    open={openSubMenu}
                    hasMenuItems={!!menuItem.menuItems}
                    onClose={undefined}
                    handleClick={
                      !!menuItem.menuItems && ((e) => this.handleSubMenuClick(e, menuItem.text))
                    }
                    style={{ color: 'black' }}
                    text={menuItem.text}
                    tabIndex={1}
                    className={clsx(
                      classes.menuItemDropdown,
                      (menuItem.active ?? checkIsActive?.(menuItem)) && classes.selected
                    )}
                    slots={slots}
                    slotProps={slotProps}
                  />,
                  menuItem.menuItems ? (
                    <PopperMenu
                      open={openSubMenu && selectedSubMenu === menuItem.text}
                      onClose={this.handleSubMenuRequestClose}
                      anchorEl={subMenuAnchorEl}
                      placement="right-start"
                      classes={{
                        paper: classes.paper,
                      }}
                      disableClickAwayListener
                      popperOptions={{ strategy: 'fixed' }}
                    >
                      {menuItem.menuItems.map((subMenuItem, j) =>
                        Object.keys(subMenuItem).length === 0 ? (
                          <Divider className={classes.divider} key={`${i}_${j}`} />
                        ) : subMenuItem.isTitle ? (
                          <ListSubheader
                            key={`${i}_${j}`}
                            className={classes.isTitle}
                            data-attribute-title
                          >
                            <Typography variant="bodyDefault" emphasis="higher">
                              {subMenuItem.text}
                            </Typography>
                          </ListSubheader>
                        ) : (
                          <MenuItem
                            disableRipple
                            className={clsx(
                              (subMenuItem.active ?? checkIsActive?.(subMenuItem)) &&
                                classes.selected
                            )}
                            onClick={() => {
                              subMenuItem.onClick && subMenuItem.onClick();
                              onClick && onClick(subMenuItem);
                              this.handleSubMenuRequestClose();
                            }}
                            key={`${i}_${j}`}
                            disabled={subMenuItem.disabled}
                            tabIndex={1}
                            autoFocus={j === 0 && true}
                          >
                            <Box
                              component={slots?.NavigationMenuItem ?? 'a'}
                              {...slotProps?.NavigationMenuItem}
                              className={clsx(
                                classes.link,
                                slotProps?.NavigationMenuItem?.className
                              )}
                              href={subMenuItem.href}
                            >
                              {subMenuItem.icon && <Icon position="left">{subMenuItem.icon}</Icon>}
                              {subMenuItem.text}
                            </Box>
                          </MenuItem>
                        )
                      )}
                    </PopperMenu>
                  ) : null,
                ]
              ) : (
                <MenuItem
                  disableRipple
                  className={clsx(
                    classes.NavBarMenuItem,
                    (menuItem.active ?? checkIsActive?.(menuItem)) && classes.selected
                  )}
                  onClick={() => {
                    menuItem.onClick && menuItem.onClick();
                    onClick && onClick(menuItem);
                    this.handleRequestClose();
                  }}
                  key={i}
                  disabled={menuItem.disabled}
                >
                  <Box
                    component={slots?.NavigationMenuItem ?? 'a'}
                    {...slotProps?.NavigationMenuItem}
                    className={clsx(classes.link, slotProps?.NavigationMenuItem?.className)}
                    href={menuItem.href}
                  >
                    {menuItem.icon && <Icon position="left">{menuItem.icon}</Icon>}
                    {menuItem.text}
                  </Box>
                </MenuItem>
              )
            )}
          </PopperMenu>
        )}
      </>
    );
  }
}

export default withStyles(NavigationMenuItem, styles);
